import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { shortUuid } from "@egalregal/shared";
import { ShortUuidIcons } from "./ShortUuidIcons";
import { HexScalableText } from "./HexScalableText";
import { QrPng } from "./QrPng";
import { QrSvg } from "../lib/QrSvg";

export type LabelLayout = "stack" | "row" | "split";

/** How the margin values map onto the label's four edges (ADR-0032, ticket 07). */
export type MarginMode = "uniform" | "xy" | "edges";

export interface LabelConfig {
  layout: LabelLayout;
  showIcons: boolean;
  showQr: boolean;
  showShortId: boolean;
  marginMm: number;
  gapMm: number;
  /** Defaults to "uniform"; configs without it behave exactly like before ticket 07. */
  marginMode?: MarginMode;
  marginXMm?: number;
  marginYMm?: number;
  marginTopMm?: number;
  marginRightMm?: number;
  marginBottomMm?: number;
  marginLeftMm?: number;
}

/**
 * Resolve the four outer padding edges of a label cell in mm — the single
 * resolver shared by all three layouts (ADR-0032, ticket 07).
 *
 * @param config label config; `marginMode` picks the mapping and any missing
 *   per-mode field falls back to `marginMm`, so pre-ticket-07 configs keep
 *   uniform padding.
 * @returns the top/right/bottom/left margins in millimetres.
 */
export function resolveMargins(config: LabelConfig): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  const { marginMm, marginMode = "uniform" } = config;
  if (marginMode === "xy") {
    return {
      top: config.marginYMm ?? marginMm,
      right: config.marginXMm ?? marginMm,
      bottom: config.marginYMm ?? marginMm,
      left: config.marginXMm ?? marginMm,
    };
  }
  if (marginMode === "edges") {
    return {
      top: config.marginTopMm ?? marginMm,
      right: config.marginRightMm ?? marginMm,
      bottom: config.marginBottomMm ?? marginMm,
      left: config.marginLeftMm ?? marginMm,
    };
  }
  return { top: marginMm, right: marginMm, bottom: marginMm, left: marginMm };
}

export interface LabelCellProps {
  uuid: string;
  name: string;
  className?: string;
  config?: LabelConfig;
  /** Sheet columns used to estimate the QR edge (ticket 09); default 3. */
  columns?: number;
}

/**
 * Estimate the QR edge length in mm (ticket 09): the ≈190mm printable sheet
 * width shared across `columns` labels, taken at the layout's pinned QR
 * fraction — stack 1.0, row 0.25 (first track of `1fr 4fr 4fr`), split 1/3
 * (`1fr 2fr`). Below 20mm the cell switches from the SVG to the PNG renderer.
 *
 * @param config label config; only `layout` picks the fraction.
 * @param columns number of label columns on the sheet (default 3).
 * @returns the estimated QR edge length in millimetres.
 */
export function qrEdgeMm(config: LabelConfig, columns = 3): number {
  const fraction = config.layout === "stack" ? 1 : config.layout === "row" ? 0.25 : 1 / 3;
  return fraction * (190 / columns);
}



/**
 * One printed label (ADR-0027). W-model: card width-driven. QR rendered as SVG
 * for crisp print, or as PNG when the estimated edge falls below 20mm (ticket
 * 09); hex text rendered as SVG textLength=100%. Toggle redistribution
 * per agreed rules.
 */
export function LabelCell({
  uuid,
  name,
  className,
  columns = 3,
  config = {
    layout: "stack",
    showIcons: true,
    showQr: true,
    showShortId: true,
    marginMm: 1,
    gapMm: 1,
  },
}: LabelCellProps) {
  const hexId = shortUuid(uuid);
  const gap = `${config.gapMm}mm`;
  const edges = resolveMargins(config);
  const padding = {
    pt: `${edges.top}mm`,
    pr: `${edges.right}mm`,
    pb: `${edges.bottom}mm`,
    pl: `${edges.left}mm`,
  };
  const qrNode =
    qrEdgeMm(config, columns) < 20 ? (
      <QrPng text={uuid} aria-label={`QR ${name}`} />
    ) : (
      <QrSvg text={uuid} margin={1} aria-label={`QR ${name}`} />
    );

  if (config.layout === "stack") {
    return (
      <Box
        className={className}
        sx={{
          width: "100%",
          textAlign: "center",
          border: 1,
          borderColor: "divider",
          ...padding,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap }}>
          {config.showIcons && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              data-testid="stack-icons"
            >
              <Box sx={{ width: "100%", aspectRatio: "5 / 1" }}>
                <ShortUuidIcons uuid={uuid} stretch />
              </Box>
            </Box>
          )}
          {config.showQr && (
            <Box
              sx={{ width: "100%", aspectRatio: "1 / 1" }}
              data-testid="qr-canvas-container"
            >
              {qrNode}
            </Box>
          )}
          {config.showShortId && (
            <Box
              sx={{ width: "100%", aspectRatio: "5 / 1", display: "flex", alignItems: "stretch" }}
              data-testid="label-short-uuid-wrapper"
            >
              <HexScalableText text={hexId} />
            </Box>
          )}
        </Box>
        {name && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: gap,
              fontSize: "0.5rem",
              wordBreak: "break-word",
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
    );
  }

  if (config.layout === "row") {
    const qr = config.showQr;
    const icons = config.showIcons;
    const id = config.showShortId;
    let gridTemplate = "1fr 2fr 2fr";
    if (qr && !icons && !id) gridTemplate = "1fr";
    else if (!qr && icons && !id) gridTemplate = "1fr";
    else if (!qr && !icons && id) gridTemplate = "1fr";
    else if (!qr && icons && id) gridTemplate = "1fr 1fr";
    else if (qr && icons && !id) gridTemplate = "1fr 2fr";
    else if (qr && !icons && id) gridTemplate = "1fr 2fr";
    return (
      <Box
        className={className}
        sx={{
          width: "100%",
          textAlign: "center",
          border: 1,
          borderColor: "divider",
          ...padding,
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            gap,
            alignItems: "center",
          }}
        >
          {qr && (
            <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
              {qrNode}
            </Box>
          )}
          {icons && (
            <Box
              sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
              data-testid="row-icons"
            >
              <Box sx={{ width: "100%", aspectRatio: "5 / 1" }}>
                <ShortUuidIcons uuid={uuid} stretch />
              </Box>
            </Box>
          )}
          {id && (
            <Box
              sx={{ width: "100%", aspectRatio: "5 / 1", display: "flex", alignItems: "stretch" }}
              data-testid="label-short-uuid-wrapper"
            >
              <HexScalableText text={hexId} />
            </Box>
          )}
        </Box>
        {name && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: gap,
              fontSize: "0.5rem",
              wordBreak: "break-word",
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
    );
  }

  // split layout
  const qr = config.showQr;
  const icons = config.showIcons;
  const id = config.showShortId;
  let gridTemplate = "1fr 2fr";
  if (!qr && (icons || id)) gridTemplate = "1fr";
  if (qr && !icons && !id) gridTemplate = "1fr";
  if (qr && (icons || id)) gridTemplate = "1fr 2fr";
  if (!qr) gridTemplate = "1fr";
  return (
    <Box
      className={className}
      sx={{
        width: "100%",
        textAlign: "center",
        border: 1,
        borderColor: "divider",
        ...padding,
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap,
          alignItems: "stretch",
        }}
      >
        {qr && (
          <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
            {qrNode}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap,
            minWidth: 0,
          }}
        >
          {icons && (
            <Box
              sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
              data-testid="split-icons"
            >
              <Box sx={{ width: "100%", aspectRatio: "5 / 1" }}>
                <ShortUuidIcons uuid={uuid} stretch />
              </Box>
            </Box>
          )}
          {id && (
            <Box
              sx={{ width: "100%", aspectRatio: "5 / 1", display: "flex", alignItems: "stretch" }}
              data-testid="label-short-uuid-wrapper"
            >
              <HexScalableText text={hexId} />
            </Box>
          )}
        </Box>
      </Box>
      {name && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: gap,
            fontSize: "0.5rem",
            wordBreak: "break-word",
          }}
        >
          {name}
        </Typography>
      )}
    </Box>
  );
}
